import {
  Home,
  Wrench,
  Droplets,
  PanelLeft,
  SquareStack,
  Settings,
  Users,
  Megaphone,
  Globe,
  DoorOpen,
  UserCheck,
  User,
  Phone,
  Mail,
  Hash,
  DollarSign,
  Briefcase,
  MapPin,
  Ruler,
  FileText,
  Receipt,
  MessageSquare,
  Calendar,
  Activity,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type {
  JobType,
  ContactSource,
} from "../providers/fakerest/dataGenerator/types";

// Job Type configuration
export interface JobTypeConfig {
  id: JobType;
  label: string;
  shortLabel: string; // For compact views like Kanban cards
  icon: LucideIcon;
  color: string;
  badgeClass: string;
}

export const JOB_TYPES: JobTypeConfig[] = [
  {
    id: "roof_replacement",
    label: "Roof Replacement",
    shortLabel: "Roof",
    icon: Home,
    color: "text-blue-600 dark:text-blue-400",
    badgeClass:
      "bg-blue-100 text-blue-800 dark:bg-blue-800/50 dark:text-blue-300",
  },
  {
    id: "roof_repair",
    label: "Roof Repair",
    shortLabel: "Repair",
    icon: Wrench,
    color: "text-sky-600 dark:text-sky-400",
    badgeClass: "bg-sky-100 text-sky-800 dark:bg-sky-800/50 dark:text-sky-300",
  },
  {
    id: "gutter",
    label: "Gutter",
    shortLabel: "Gutter",
    icon: Droplets,
    color: "text-teal-600 dark:text-teal-400",
    badgeClass:
      "bg-teal-100 text-teal-800 dark:bg-teal-800/50 dark:text-teal-300",
  },
  {
    id: "siding",
    label: "Siding",
    shortLabel: "Siding",
    icon: PanelLeft,
    color: "text-amber-600 dark:text-amber-400",
    badgeClass:
      "bg-amber-100 text-amber-800 dark:bg-amber-800/50 dark:text-amber-300",
  },
  {
    id: "window",
    label: "Window",
    shortLabel: "Window",
    icon: SquareStack,
    color: "text-violet-600 dark:text-violet-400",
    badgeClass:
      "bg-violet-100 text-violet-800 dark:bg-violet-800/50 dark:text-violet-300",
  },
  {
    id: "general",
    label: "General",
    shortLabel: "General",
    icon: Settings,
    color: "text-gray-600 dark:text-gray-400",
    badgeClass: "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200",
  },
];

export const getJobType = (id: JobType): JobTypeConfig | undefined =>
  JOB_TYPES.find((t) => t.id === id);

export const getJobTypeLabel = (id: JobType): string =>
  getJobType(id)?.label ?? id;

export const getJobTypeShortLabel = (id: JobType): string =>
  getJobType(id)?.shortLabel ?? id;

export const getJobTypeBadgeClass = (id: JobType): string =>
  getJobType(id)?.badgeClass ??
  "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200";

export const getJobTypeIcon = (id: JobType): LucideIcon =>
  getJobType(id)?.icon ?? Settings;

// Contact Source configuration
export interface ContactSourceConfig {
  id: ContactSource;
  label: string;
  icon: LucideIcon;
  color: string;
  badgeClass: string;
}

export const CONTACT_SOURCES: ContactSourceConfig[] = [
  {
    id: "referral",
    label: "Referral",
    icon: Users,
    color: "text-green-600 dark:text-green-400",
    badgeClass:
      "bg-green-100 text-green-800 dark:bg-green-800/50 dark:text-green-300",
  },
  {
    id: "ad",
    label: "Ad",
    icon: Megaphone,
    color: "text-blue-600 dark:text-blue-400",
    badgeClass:
      "bg-blue-100 text-blue-800 dark:bg-blue-800/50 dark:text-blue-300",
  },
  {
    id: "website",
    label: "Website",
    icon: Globe,
    color: "text-purple-600 dark:text-purple-400",
    badgeClass:
      "bg-purple-100 text-purple-800 dark:bg-purple-800/50 dark:text-purple-300",
  },
  {
    id: "door_knock",
    label: "Door Knock",
    icon: DoorOpen,
    color: "text-orange-600 dark:text-orange-400",
    badgeClass:
      "bg-orange-100 text-orange-800 dark:bg-orange-800/50 dark:text-orange-300",
  },
  {
    id: "repeat_customer",
    label: "Repeat Customer",
    icon: UserCheck,
    color: "text-teal-600 dark:text-teal-400",
    badgeClass:
      "bg-teal-100 text-teal-800 dark:bg-teal-800/50 dark:text-teal-300",
  },
];

export const getContactSource = (
  id: ContactSource,
): ContactSourceConfig | undefined => CONTACT_SOURCES.find((s) => s.id === id);

export const getContactSourceLabel = (id: ContactSource): string =>
  getContactSource(id)?.label ?? id;

export const getContactSourceBadgeClass = (id: ContactSource): string =>
  getContactSource(id)?.badgeClass ??
  "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200";

export const getContactSourceIcon = (id: ContactSource): LucideIcon =>
  getContactSource(id)?.icon ?? Users;

// Common field icons for table columns
export const FIELD_ICONS = {
  name: User,
  phone: Phone,
  email: Mail,
  source: Users,
  jobCount: Hash,
  totalValue: DollarSign,
  jobType: Briefcase,
  address: MapPin,
  roofSqft: Ruler,
  status: Activity,
  proposals: FileText,
  invoices: Receipt,
  notes: MessageSquare,
  date: Calendar,
} as const;
